import { useCallback, useEffect, useRef, useState } from 'react';

// Switching between the demo's example households, keeping each one's work to
// itself.
//
// A reviewer will type into one example, draw on it, then pick another and come
// back. If the store were simply overwritten each time, the first example's
// edits would be gone, and a demo that loses work is a demo of the wrong thing.
// So each example keeps its own copy under its own key, the live store is
// written back to that key before a switch, and Reset restores the pristine
// fixture for the selected example only.
//
// The fixtures are loaded with a dynamic import rather than a static one, so
// they become their own chunk that only the demo build ever asks for. A static
// import would put seven fictional households into the production bundle even
// with the selector gated off, and invented client-shaped data has no business
// being shipped to a client-facing deployment at all.
//
// This is browser storage and nothing else. The demo build carries no lead id,
// so the Salesforce sync never starts and no fixture can reach a real record.

const KEY = 'pure-assessment-demo-v1';
const CURRENT_KEY = 'pure-assessment-demo-current';

function readAll() {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(map) {
  try {
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    // A full or blocked store is not worth breaking the demo over. The session
    // simply stops remembering, which is visible and harmless.
  }
}

/**
 * @param enabled  only ever true on the demo build
 * @param data     the live store
 * @param replace  swaps the whole store for the given household
 */
export default function useDemoExamples({ enabled, data, replace }) {
  const [households, setHouseholds] = useState(null);
  const [current, setCurrent] = useState('');
  const modRef = useRef(null);
  const dataRef = useRef(data);
  dataRef.current = data;


  useEffect(() => {
    if (!enabled) return;
    let live = true;
    import('./demoHouseholds.js').then(mod => {
      if (!live) return;
      modRef.current = mod;
      let id = mod.DEFAULT_DEMO_ID;
      try {
        const saved = localStorage.getItem(CURRENT_KEY);
        if (mod.DEMO_HOUSEHOLDS.some(h => h.id === saved)) id = saved;
      } catch { /* default stands */ }
      const stored = readAll()[id];
      replace(stored || mod.demoHouseholdData(id));
      setHouseholds(mod.DEMO_HOUSEHOLDS);
      setCurrent(id);
    });
    return () => { live = false; };
  }, [enabled, replace]);

  useEffect(() => {
    if (!enabled || !current || !households) return;
    // A switch must never write the preceding household under the new key.
    if(data?._uid !== modRef.current?.DEMO_HOUSEHOLDS.find(h=>h.id===current)?.data?._uid) return;
    const map=readAll();map[current]=data;writeAll(map);
    try { localStorage.setItem(CURRENT_KEY,current); } catch { /* session only */ }
  }, [enabled,current,households,data]);

  const select = useCallback(
    id => {
      const mod = modRef.current;
      if (!enabled || !mod || id === current) return;
      const map = readAll();
      map[current] = dataRef.current;
      writeAll(map);
      try { localStorage.setItem(CURRENT_KEY, id); } catch { /* not fatal */ }
      replace(map[id] || mod.demoHouseholdData(id));
      setCurrent(id);
    },
    [enabled, current, replace],
  );

  // Reset restores the fixture for the selected example and leaves every other
  // example's work alone, which is what "this example" has to mean.
  const reset = useCallback(() => {
    const mod = modRef.current;
    if (!enabled || !mod) return;
    const map = readAll();
    delete map[current];
    writeAll(map);
    replace(mod.demoHouseholdData(current));
  }, [enabled, current, replace]);

  return { households, current, select, reset, ready: Boolean(households) };
}
